import React, { useState } from "react";
import { EventTypeResponse } from "../../../app/models/responseModels/eventTypeResponse";
import { UpdateEventTypeRequest } from "../../../app/models/requestModels/eventTypeRequest";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { useSelector } from "react-redux";
import { PAGES } from "../../../enums/PagesEnum";
import { toast } from "react-toastify";
import getAxiosError from "../../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../../app/features/login/loginSlice";
import { Box, Grid, TextField } from "@mui/material";
import SubmitModalWithEditAndDelete from "../../../components/common/modalPopup/SubmitModalWithEditAndDelete";
import { updateEventType } from "../../../app/features/eventType/eventTypeSlice";

interface EditEventTypeModalProps {
  closeEditEventTypeModal: CallableFunction;
  onCompleted: CallableFunction;
  eventTypeRowData: EventTypeResponse;
}

function UpdateEventTypeModel(
  id: string,
  name: string,
  category: string,
  description: string
): UpdateEventTypeRequest {
  return {
    id: id,
    name: name,
    category: category,
    description: description,
  };
}

const EditEventTypeModal = ({
  closeEditEventTypeModal,
  onCompleted,
  eventTypeRowData,
}: EditEventTypeModalProps) => {
  const dispatch = useAppDispatch();

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.EVENT_TYPE
    )
  );

  const IsUpdatePermisssion = pagePermission
    ? pagePermission.isUpdatePermission
    : false;

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        )
      : false
  );

  // ***** States *****
  const [showSubmitButton, setShowSubmitButton] = useState(false);
  const [name, setName] = useState(eventTypeRowData.name);
  const [category, setCategory] = useState(eventTypeRowData.category);
  const [description, setDescription] = useState(eventTypeRowData.description);

  // ***** Handlers *****
  const handleSubmit = async () => {
    try {
      if (name.length === 0) {
        toast.error("Event Type name is required");
        return false;
      }
      if (category.length === 0) {
        toast.error("Event Type category is required");
        return false;
      }
      const model = UpdateEventTypeModel(
        eventTypeRowData.id,
        name,
        category,
        description
      );
      const response = await dispatch(updateEventType(model)).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, {
          toastId: "updateMiddleMileSuccess",
        });
        onCompleted();
      } else {
        toast.error(response.message!, {
          toastId: "updateMiddleMileError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "updateMiddleMileException",
      });
    }
    return true;
  };

  return (
    <>
      <SubmitModalWithEditAndDelete
        title="Edit Event Type"
        updateAsyncOperation={handleSubmit}
        closeSubmitModalWithEditAndDelete={closeEditEventTypeModal}
        size="lg"
        isLoading={false}
        submitButtonName="Save"
        closeModalAfterSubmit={true}
        confirmMessage="Are you sure you want to delete?"
        isUpdatePermission={IsUpdatePermisssion && IsAdmin}
        isDeletePermission={false}
        setShowSubmitButton={setShowSubmitButton}
        showSubmitButton={showSubmitButton}
        showDeleteButton={false}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid container rowSpacing={2} columnSpacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                margin="normal"
                id="eventName"
                label="Event Name"
                name="eventName"
                size="small"
                fullWidth
                required
                disabled={!showSubmitButton}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                margin="normal"
                id="category"
                label="Category"
                name="category"
                size="small"
                fullWidth
                required
                disabled={!showSubmitButton}
                value={category}
                onChange={(event) => setCategory(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                margin="normal"
                id="description"
                label="Description"
                name="description"
                size="small"
                fullWidth
                disabled={!showSubmitButton}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Grid>
          </Grid>
        </Box>
      </SubmitModalWithEditAndDelete>
    </>
  );
};

export default EditEventTypeModal;
